import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuGroup, MenuItem, MenuResource, SubMenuItem } from '@core/layout/models/menu.model';
import { RecursosService } from '@core/services/recursos.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MenuService implements OnDestroy {
  private readonly _showSidebar = signal(true);
  private readonly _subscription = new Subscription();
  private readonly _isManualToggle = signal(false);
  private readonly _isHidden = signal(false);
  private readonly _showMobileMenu = signal(false);
  private readonly _recursoService = inject(RecursosService);
  private readonly _router = inject(Router);

  // ✅ BehaviorSubject para armazenar o menu processado
  private readonly _pagesMenu$ = new BehaviorSubject<MenuItem[]>([]);

  constructor() {
    // 🚀 Transforma `menuGroups$` automaticamente, sem `computed()`
    const sub = this._recursoService.menuGroups$
      .pipe(map((menuGroups) => this.mapMenuGroupsToMenuItems(menuGroups)))
      .subscribe(this._pagesMenu$); // Atualiza `_pagesMenu$` automaticamente

    this._subscription.add(sub);
    this.updateSidebarState();

    const navSub = this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        /** Expand menu based on active route */
        this._pagesMenu$.value.forEach((menu) => {
          let activeGroup = false;
          menu.items.forEach((subMenu) => {
            const active = this.isActive(subMenu.route);
            subMenu.expanded = active;
            subMenu.active = active;
            if (active) activeGroup = true;
            if (subMenu.children) {
              this.expand(subMenu.children);
            }
          });
          menu.active = activeGroup;
        });

        // Forçar uma nova emissão para atualizar o BehaviorSubject
        this._pagesMenu$.next([...this._pagesMenu$.value]);
      }
    });

    this._subscription.add(navSub);
    window.addEventListener('resize', this.onResize.bind(this));
  }

  /** ✅ Getters usando RxJS */
  public get pagesMenu$() {
    return this._pagesMenu$.asObservable();
  }

  public get isManualToggle() {
    return this._isManualToggle();
  }

  public set isManualToggle(value: boolean) {
    this._isManualToggle.set(value);
  }

  public get isHidden() {
    return this._isHidden();
  }

  public set isHidden(value: boolean) {
    this._isHidden.set(value);
  }

  public get showMobileMenu() {
    return this._showMobileMenu();
  }

  public set showMobileMenu(value: boolean) {
    this._showMobileMenu.set(value);
  }

  public get showSideBar() {
    return this._showSidebar();
  }

  public set showSideBar(value: boolean) {
    this._showSidebar.set(value);
  }

  /** ✅ Métodos para alternar menus */
  public toggleSidebar() {
    this.isManualToggle = !this.isManualToggle;
    this._showSidebar.set(!this._showSidebar());
  }

  public toggleMenu(menu: any) {
    this.showSideBar = true;
    menu.expanded = !menu.expanded;
  }

  public toggleSubMenu(submenu: SubMenuItem) {
    submenu.expanded = !submenu.expanded;
  }

  /** ✅ Gerenciamento de ciclo de vida */
  ngOnDestroy(): void {
    console.log('[MenuService] Destroy');
    this._subscription.unsubscribe();
    window.removeEventListener('resize', this.onResize.bind(this));
  }

  // Restaura o controle automático ao redimensionar, se necessário
  resetManualToggle(): void {
    this.isManualToggle = false;
    this.updateSidebarState();
  }

  /** ✅ Expande submenus automaticamente com base na rota ativa */
  private expand(items: SubMenuItem[]) {
    items.forEach((item) => {
      item.expanded = this.isActive(item.route);
      if (item.children) this.expand(item.children);
    });
  }

  private isActive(instruction: unknown): boolean {
    return this._router.isActive(this._router.createUrlTree([instruction]), {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  /** ✅ Conversão dos menus da API */
  private mapMenuGroupsToMenuItems(menuGroups: MenuGroup[]): MenuItem[] {
    return menuGroups.map((group) => ({
      group: group.grupo,
      alias: group.grupo.toLowerCase().replace(/\s+/g, '_'),
      items: group.recursos.map((resource) => this.mapResourceToSubMenuItem(resource)),
      separator: false,
      selected: false,
      active: false,
    }));
  }

  private mapResourceToSubMenuItem(resource: MenuResource): SubMenuItem {
    return {
      label: resource.nome,
      route: resource.rota,
      icon: resource.icone || '',
      expanded: false,
      active: false,
      children: [],
    };
  }

  /** ✅ Atualiza o estado da sidebar conforme o tamanho da tela */
  private updateSidebarState(): void {
    if (!this.isManualToggle) {
      const width = window.innerWidth;
      if (width < 768) {
        this.isHidden = true;
        this.showSideBar = false;
      } else if (width >= 768 && width < 1024) {
        this.isHidden = false;
        this.showSideBar = false;
      } else {
        this.isHidden = false;
        this.showSideBar = true;
      }
    }
  }

  /** ✅ Listener de resize */
  private onResize(): void {
    this.updateSidebarState();
  }
}
